import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/config';
import './ResetPasswordPage.css';

const validatePassword = (pass) => {
  const hasMinLength = pass.length >= 8;
  const hasMaxLength = pass.length <= 20;
  const hasUpperCase = /[A-Z]/.test(pass);
  const hasLowerCase = /[a-z]/.test(pass);
  const hasNumber = /[0-9]/.test(pass);
  const hasSymbol = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass);

  return {
    isValid: hasMinLength && hasMaxLength && hasUpperCase && hasLowerCase && hasNumber && hasSymbol,
    hasMinLength,
    hasMaxLength,
    hasUpperCase,
    hasLowerCase,
    hasNumber,
    hasSymbol
  };
};

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de recuperación no válido');
    }
  }, [token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const passwordValidation = validatePassword(formData.newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (!passwordValidation.isValid) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!token) {
      setError('Token de recuperación no válido');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword: formData.newPassword
      });

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(response.data.message || 'Error al restablecer la contraseña');
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(
        err.response?.data?.message ||
        'Error al restablecer la contraseña. El enlace puede haber expirado.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1>PAPER BOAT</h1>
          <h2>Restablecer Contraseña</h2>
          <p>Ingresa tu nueva contraseña</p>
        </div>

        {success ? (
          <div className="reset-password-success">
            <div className="success-icon">✓</div>
            <h3>¡Contraseña actualizada exitosamente!</h3>
            <p>Redirigiendo al login...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="input-group">
              <label>Nueva Contraseña</label>
              <div className="input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="8-20 caracteres"
                  disabled={loading || !token}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {formData.newPassword && (
              <div className="password-requirements">
                <ul>
                  <li className={passwordValidation.hasMinLength ? 'req-valid' : 'req-invalid'}>Mínimo 8 caracteres</li>
                  <li className={passwordValidation.hasMaxLength ? 'req-valid' : 'req-invalid'}>Máximo 20 caracteres</li>
                  <li className={passwordValidation.hasUpperCase ? 'req-valid' : 'req-invalid'}>Al menos una mayúscula</li>
                  <li className={passwordValidation.hasLowerCase ? 'req-valid' : 'req-invalid'}>Al menos una minúscula</li>
                  <li className={passwordValidation.hasNumber ? 'req-valid' : 'req-invalid'}>Al menos un número</li>
                  <li className={passwordValidation.hasSymbol ? 'req-valid' : 'req-invalid'}>Al menos un símbolo (!@#$%^&*)</li>
                </ul>
              </div>
            )}

            <div className="input-group">
              <label>Confirmar Contraseña</label>
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Repite tu contraseña"
                  disabled={loading || !token}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                      <line x1="1" y1="1" x2="23" y2="23"></line>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <div className="password-mismatch">Las contraseñas no coinciden</div>
            )}

            {error && (
              <div className="error-message">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={loading || !token || !passwordValidation.isValid || formData.newPassword !== formData.confirmPassword || !formData.confirmPassword}
            >
              {loading ? (
                <div className="spinner"></div>
              ) : (
                'Restablecer Contraseña'
              )}
            </button>

            <button
              type="button"
              className="back-to-login"
              onClick={() => navigate('/login')}
            >
              ← Volver al Login
            </button>
          </form>
        )}
      </div>

      <div className="reset-password-footer">
        <p>© {new Date().getFullYear()} Paper Boat - Todos los derechos reservados</p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
